import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { AccountInfoLabel, BoxFlex, CheckIcon } from '@woovi/ui';
import { convertToMoney } from '@woovi/utils';

import PaymentInstallmentPlan from '../../installment/PaymentInstallmentPlan.tsx';
import PaymentLinkAdditionalInfo from '../../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkTransactionDetails from '../../PaymentLinkTransactionDetails.tsx';
import type { PaymentLinkCompleted_charge$key } from './__generated__/PaymentLinkCompleted_charge.graphql.ts';
import type { PaymentLinkCompleted_query$key } from './__generated__/PaymentLinkCompleted_query.graphql.ts';
import PaymentLinkCompletedPaymentMethods from './PaymentLinkCompletedPaymentMethods.tsx';
import PaymentLinkCompletedPixCreditCard from './PaymentLinkCompletedPixCreditCard.tsx';
import PaymentLinkCompletedQrCodeValue from './PaymentLinkCompletedQrCodeValue.tsx';
import PaymentLinkCompletedRedirect from './PaymentLinkCompletedRedirect.tsx';
import PaymentLinkCompletedRewards from './PaymentLinkCompletedRewards.tsx';
import PaymentLinkCompletedTransaction from './PaymentLinkCompletedTransaction.tsx';

type PaymentLinkCompletedProps = {
  query: PaymentLinkCompleted_query$key;
  charge: PaymentLinkCompleted_charge$key;
};

const PaymentLinkCompleted = (props: PaymentLinkCompletedProps): ReactNode => {
  const { t } = useTranslation();

  const query = useFragment<PaymentLinkCompleted_query$key>(
    graphql`
      fragment PaymentLinkCompleted_query on Query {
        ...PaymentLinkCompletedPixCreditCard_query
      }
    `,
    props.query,
  );

  const charge = useFragment<PaymentLinkCompleted_charge$key>(
    graphql`
      fragment PaymentLinkCompleted_charge on Charge {
        id
        discount
        identifier
        redirectUrl
        company {
          nameFriendly
          taxID {
            taxID
          }
        }
        ...PaymentLinkCompletedQrCodeValue_charge
        ...PaymentInstallmentPlan_charge
        ...PaymentLinkCompletedTransaction_charge
        ...PaymentLinkAdditionalInfo_charge
        ...PaymentLinkCompletedRewards_charge
        ...PaymentLinkTransactionDetails_charge
        ...PaymentLinkCompletedPixCreditCard_charge
        ...PaymentLinkCompletedPaymentMethods_charge
        ...PaymentLinkCompletedRedirect_charge
      }
    `,
    props.charge,
  );

  return (
    <Stack spacing={2} alignItems='center' sx={{ maxWidth: '100%' }}>
      <BoxFlex sx={{ flexDirection: 'column', alignItems: 'center' }}>
        <CheckIcon />
      </BoxFlex>
      <Typography
        variant={'h2'}
        sx={{ fontSize: '22px', fontWeight: 'bold', textAlign: 'center' }}
      >
        {t('Payment confirmed')}
      </Typography>
      <PaymentLinkCompletedRedirect charge={charge} />
      <PaymentLinkCompletedQrCodeValue charge={charge} />
      <PaymentLinkCompletedPaymentMethods charge={charge} />
      <PaymentInstallmentPlan charge={charge} />
      {charge?.discount > 0 && (
        <AccountInfoLabel
          label={t('Discount')}
          text={convertToMoney(charge.discount)}
        />
      )}
      <PaymentLinkCompletedPixCreditCard query={query} charge={charge} />
      <PaymentLinkTransactionDetails charge={charge} />
      <PaymentLinkCompletedTransaction charge={charge} />
      <PaymentLinkAdditionalInfo charge={charge} />
      <PaymentLinkCompletedRewards charge={charge} />
    </Stack>
  );
};

export default PaymentLinkCompleted;
